/**
 * The one value of an async iterable. Throws as soon as a second one shows up
 * (the source is closed there, nothing past it is read), or at the end when
 * there was none.
 *
 * @param source - The async iterable expected to hold exactly one value
 * @returns A promise that resolves to that value
 *
 * @example
 * ```ts
 * import { Only } from "@pdfking/tfunc"
 *
 * async function* one() {
 *   yield 1
 * }
 *
 * await Only(one()) // 1
 * ```
 */
export const Only = async <T>(source: AsyncIterable<T>): Promise<T> => {
  const items: T[] = []
  for await (const item of source) {
    if (items.length) throw new Error("Expected exactly 1 element, got more")
    items.push(item)
  }
  if (!items.length) throw new Error("Expected exactly 1 element, got 0")
  return items[0]!
}
